import { getInventoryTransition } from '../api/warehouseApi';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import InlineLoading from "./InlineLoading";
import CategoryFormatSelector from './CategoryFormatSelector';

const buildAvailabilityState = (entries) => {
  const next = {};
  if (!Array.isArray(entries)) return next;
  entries.forEach((entry) => {
    const branchId = entry?.branchId;
    if (!branchId) return;
    const key = String(branchId);
    if (!next[key]) {
      next[key] = { all: false, cashRegisterIds: [] };
    }
    if (!entry.cashRegisterId) {
      next[key].all = true;
    } else {
      const id = String(entry.cashRegisterId);
      if (!next[key].cashRegisterIds.includes(id)) {
        next[key].cashRegisterIds.push(id);
      }
    }
  });
  return next;
};

const CategoryFormModal = ({
  isOpen,
  onClose,
  onSave,
  branches = [],
  cashRegistersByBranch = {},
  initialData,
  isSaving,
  submitError,
  canUpdatePrices = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isResaleOnly, setIsResaleOnly] = useState(false);
  const [tracksInventory, setTracksInventory] = useState(false);
  const [inventoryTrackingMode, setInventoryTrackingMode] = useState('standard');
  const [standardCounts, setStandardCounts] = useState([]);
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [pricingBasis, setPricingBasis] = useState("unit");
  const [prefillProductFormatIds, setPrefillProductFormatIds] = useState(null);
  const [availability, setAvailability] = useState({});
  const [error, setError] = useState("");
  const dialogRef = useRef(null);
  const nameInputRef = useRef(null);
  const previousFocusRef = useRef(null);

  const availabilityEntries = useMemo(() => {
    return (
      initialData?.CategoryAvailabilities
      || initialData?.CategoryAvailability
      || []
    );
  }, [initialData]);

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setName(initialData?.name || "");
    setDescription(initialData?.description || "");
    setIsActive(initialData?.isActive ?? true);
    setIsResaleOnly(Boolean(initialData?.isResaleOnly));
    setTracksInventory(Boolean(initialData?.tracksInventory));
    setInventoryTrackingMode(initialData?.inventoryTrackingMode || 'standard');
    setStandardCounts([]);
    setPricingBasis(initialData?.pricingBasis === "kg" ? "kg" : "unit");
    setPrefillProductFormatIds(initialData?.prefillProductFormatIds ?? null);
    setAvailability(buildAvailabilityState(availabilityEntries));
  }, [availabilityEntries, initialData, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    previousFocusRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => nameInputRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isSaving) onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, isSaving, onClose]);

  const needsStandardCount = isOpen && (initialData?.inventoryTrackingMode === 'pack_warehouse' || (initialData?.inventoryPolicyId && !initialData?.tracksInventory)) && inventoryTrackingMode === 'standard' && tracksInventory;
  useEffect(() => {
    if (!needsStandardCount) return undefined;
    let active = true; setTransitionLoading(true);
    getInventoryTransition(initialData.id).then(rows => { if (active) setStandardCounts(rows.map(row => ({ ...row, quantity: '' }))); })
      .catch(() => { if (active) setError('No se pudo cargar el conteo inicial. Vuelve a abrir el formulario.'); })
      .finally(() => { if (active) setTransitionLoading(false); });
    return () => { active = false; };
  }, [needsStandardCount, initialData?.id]);
  if (!isOpen) return null;

  const displayedError = error || submitError;
  const initialPricingBasis = initialData?.pricingBasis === "kg" ? "kg" : "unit";
  const pricingBasisChanged = Boolean(initialData) && pricingBasis !== initialPricingBasis;

  const updateBranchAvailability = (branchId, updater) => {
    setAvailability((prev) => {
      const key = String(branchId);
      const current = prev[key] || { all: false, cashRegisterIds: [] };
      const nextValue = updater({ ...current });
      const next = { ...prev };
      if (!nextValue.all && nextValue.cashRegisterIds.length === 0) {
        delete next[key];
      } else {
        next[key] = nextValue;
      }
      return next;
    });
  };

  const toggleBranchAll = (branchId) => {
    updateBranchAvailability(branchId, (current) => {
      const nextAll = !current.all;
      return { all: nextAll, cashRegisterIds: nextAll ? [] : current.cashRegisterIds };
    });
  };

  const toggleCashRegister = (branchId, cashRegisterId) => {
    updateBranchAvailability(branchId, (current) => {
      const id = String(cashRegisterId);
      const ids = current.cashRegisterIds.includes(id)
        ? current.cashRegisterIds.filter((item) => item !== id)
        : [...current.cashRegisterIds, id];
      return { all: false, cashRegisterIds: ids };
    });
  };

  const buildAvailabilityPayload = () => {
    const payload = [];
    Object.entries(availability).forEach(([branchId, selection]) => {
      const resolvedBranchId = Number(branchId);
      if (!Number.isInteger(resolvedBranchId)) return;
      if (selection.all) {
        payload.push({ branchId: resolvedBranchId, cashRegisterId: null, isActive: true });
        return;
      }
      selection.cashRegisterIds.forEach((cashRegisterId) => {
        const resolvedId = Number(cashRegisterId);
        if (!Number.isInteger(resolvedId)) return;
        payload.push({ branchId: resolvedBranchId, cashRegisterId: resolvedId, isActive: true });
      });
    });
    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("El nombre es obligatorio.");
      return;
    }
    if (needsStandardCount && (transitionLoading || standardCounts.some(row => row.quantity === '' || Number(row.quantity) < 0))) { setError('Completa el conteo inicial.'); return; }
    const payload = {
      inventoryTrackingMode,
      ...(needsStandardCount ? { standardCounts } : {}),
      name: trimmedName,
      description: description.trim() || null,
      isActive: Boolean(isActive),
      isResaleOnly: Boolean(isResaleOnly),
      tracksInventory: Boolean(tracksInventory),
      prefillProductFormatIds,
      ...(canUpdatePrices
        ? { pricingBasis: pricingBasis === "kg" ? "kg" : "unit" }
        : {}),
      availability: buildAvailabilityPayload(),
    };
    onSave(payload);
  };

  const requestClose = () => {
    if (!isSaving) onClose?.();
  };

  const handleDialogKeyDown = (event) => {
    if (event.key !== "Tab") return;
    const focusable = Array.from(dialogRef.current.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1700] flex items-stretch justify-center bg-slate-950/65 p-0 backdrop-blur-[1px] sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="category-form-dialog-title"
        aria-describedby="category-form-dialog-description"
        aria-busy={isSaving}
        className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:max-w-4xl sm:rounded-2xl sm:border sm:border-gray-100"
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <header
          className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 bg-gray-50 px-4 pb-4 pt-4 sm:px-6"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Categorias</p>
            <h2 id="category-form-dialog-title" className="mt-1 text-xl font-semibold text-gray-950">
              {initialData ? "Editar categoria" : "Nueva categoria"}
            </h2>
            <p id="category-form-dialog-description" className="mt-1 text-sm text-gray-600">
              Define su calculo de precios, estado y disponibilidad por sucursal y caja.
            </p>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSaving}
            aria-label="Cerrar formulario de categoria"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6">
            <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div>
                <label htmlFor="category-form-name" className="text-xs font-semibold uppercase text-gray-600">Nombre</label>
                <input
                  ref={nameInputRef}
                  id="category-form-name"
                  type="text"
                  className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError("");
                  }}
                  disabled={isSaving}
                />
                <label htmlFor="category-form-description" className="mt-4 block text-xs font-semibold uppercase text-gray-600">Descripcion</label>
                <textarea
                  id="category-form-description"
                  rows={3}
                  className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-2">
                {[
                  { label: "Categoria activa", checked: isActive, setter: setIsActive },
                  { label: "Reventa sin conversion", checked: isResaleOnly, setter: setIsResaleOnly },
                ].map((option) => (
                  <label key={option.label} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={option.checked}
                      onChange={(e) => option.setter(e.target.checked)}
                      disabled={isSaving}
                      className="accent-blue-600"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </section>

            <label className="block text-sm font-semibold">Inventario
              <select aria-label="Inventario" className="mt-2 w-full rounded-xl border p-3" disabled={isSaving}
                value={tracksInventory ? inventoryTrackingMode : 'none'} onChange={event => { setTracksInventory(event.target.value !== 'none'); setInventoryTrackingMode(event.target.value === 'pack_warehouse' ? 'pack_warehouse' : 'standard'); }}>
                <option value="none">Sin control</option><option value="standard">Inventario actual</option><option value="pack_warehouse">Bodega por packs</option>
              </select>
            </label>
            {tracksInventory && inventoryTrackingMode === 'pack_warehouse' && <p className="text-sm text-slate-600">Packs enteros por sucursal. Al activar, el saldo comienza en cero y se cuentan los nuevos ingresos.</p>}
            {needsStandardCount && <fieldset className="space-y-3 rounded-xl border p-3"><legend>Conteo inicial por sucursal</legend>
              {transitionLoading ? <InlineLoading /> : standardCounts.map((row, index) => <label key={`${row.branchId}:${row.varietyId}`} className="block text-sm">{row.branchName} · {row.productName} · {row.varietyName} ({row.unit})
                <input type="number" min="0" step="any" inputMode="decimal" className="mt-1 w-full rounded border p-2" value={row.quantity}
                  onChange={event => setStandardCounts(current => current.map((entry, i) => i === index ? { ...entry, quantity: event.target.value } : entry))} />
              </label>)}
            </fieldset>}
            <CategoryFormatSelector value={prefillProductFormatIds} onChange={setPrefillProductFormatIds} disabled={isSaving} />

            <fieldset className="rounded-2xl border border-blue-100 bg-blue-50/60 p-4">
              <legend className="px-1 text-sm font-semibold text-gray-950">Base de precios y margenes</legend>
              <p className="mt-1 text-xs leading-relaxed text-gray-600">
                Define si los costos y reglas de precio se expresan por unidad o por kilogramo. Cada producto puede reemplazar este valor.
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {[
                  { value: "unit", title: "Por unidad", detail: "Mantiene costos y margenes por formato vendido." },
                  { value: "kg", title: "Por kg", detail: "Usa costo, gastos, regla y precio final por kilogramo." },
                ].map((option) => {
                  const checked = pricingBasis === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`rounded-xl border bg-white px-4 py-3 transition ${checked ? "border-blue-600 ring-2 ring-blue-100" : "border-gray-200"} ${canUpdatePrices && !isSaving ? "cursor-pointer hover:border-blue-300" : "cursor-not-allowed opacity-60"}`}
                    >
                      <span className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="pricingBasis"
                          value={option.value}
                          checked={checked}
                          onChange={(event) => setPricingBasis(event.target.value)}
                          disabled={isSaving || !canUpdatePrices}
                          className="mt-0.5 accent-blue-600"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-gray-900">{option.title}</span>
                          <span className="mt-1 block text-xs leading-relaxed text-gray-500">{option.detail}</span>
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {!canUpdatePrices && (
                <p className="mt-3 text-xs leading-relaxed text-gray-500">
                  Solo lectura: necesitas permiso para actualizar precios.
                </p>
              )}
              {canUpdatePrices && pricingBasisChanged && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800">
                  Al guardar se convertiran los precios y reglas de los productos que heredan esta categoria. Los formatos sin equivalencia de peso quedaran pendientes de completar.
                </p>
              )}
            </fieldset>

            <section>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-950">Disponibilidad por sucursal y caja</h3>
                <p className="mt-1 text-xs text-gray-500">Marca “Todas las cajas” o selecciona cajas especificas.</p>
              </div>
              <div className="space-y-3">
                {branches.map((branch) => {
                  const branchKey = String(branch.id);
                  const branchSelection = availability[branchKey] || { all: false, cashRegisterIds: [] };
                  const registers = cashRegistersByBranch[branch.id] || [];
                  return (
                    <div key={branch.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h4 className="text-sm font-semibold text-gray-800">{branch.branchName}</h4>
                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={branchSelection.all}
                            onChange={() => toggleBranchAll(branch.id)}
                            disabled={isSaving}
                            className="accent-blue-600"
                          />
                          Todas las cajas
                        </label>
                      </div>
                      {registers.length === 0 ? (
                        <p className="mt-2 text-xs text-gray-500">Sin cajas registradas.</p>
                      ) : (
                        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {registers.map((register) => (
                            <label key={register.id} className="flex items-center gap-2 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={branchSelection.cashRegisterIds.includes(String(register.id))}
                                onChange={() => toggleCashRegister(branch.id, register.id)}
                                disabled={isSaving || branchSelection.all}
                                className="accent-blue-600"
                              />
                              {register.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!branches.length && <p className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">No hay sucursales disponibles.</p>}
              </div>
            </section>

            {displayedError && (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{displayedError}</p>
            )}
          </div>

          <footer
            className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-200 bg-white px-4 pb-4 pt-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
          >
            <div className="text-center text-xs text-gray-500 sm:text-left">Los cambios aplican al guardar.</div>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button
                type="button"
                className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={requestClose}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
                disabled={isSaving}
              >
                {isSaving ? <InlineLoading label="Guardando" /> : initialData ? "Guardar cambios" : "Crear categoria"}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default CategoryFormModal;
